import { ExponentialBackoff, handleWhen, retry } from 'cockatiel';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Passwordless from 'supertokens-auth-react/recipe/passwordless';
import Session from 'supertokens-auth-react/recipe/session';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Icon,
  LoadingIcon,
  Text,
} from '@eluve/components';
import {
  extractResponseErrorDetails,
  formatErrorForLogging,
} from '@eluve/eluve-errors';
import { useNamedLogger } from '@eluve/logger';

const consumeCodeRetry = retry(
  handleWhen(
    (error) =>
      error instanceof TypeError ||
      (error instanceof DOMException && error.name === 'AbortError'),
  ),
  { maxAttempts: 1, backoff: new ExponentialBackoff({ initialDelay: 1000 }) },
);

const getErrorName = (error: unknown): string => {
  if (error instanceof Error) {
    return error.name;
  }
  if (error instanceof Response) {
    return 'Response';
  }
  return typeof error;
};

const getConnectionEffectiveType = (): string | undefined => {
  const connection = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection;
  return connection?.effectiveType;
};

export const MagicLinkVerifyPage = () => {
  const logger = useNamedLogger('MagicLinkVerify');
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const [magicLinkFailed, setMagicLinkFailed] = useState(false);
  const [countdown, setCountdown] = useState(5);

  // A magic-link code is single-use: a second consumeCode() gets
  // RESTART_FLOW_ERROR, flips the page into the failure UI, and its 5s
  // auto-redirect to /login races the successful login's navigation. The
  // effect re-fires in dev (React StrictMode) and whenever a dep identity
  // changes, so consume at most once per link — keyed on the URL (the code
  // lives in the #hash) so navigating to a NEW magic link still consumes.
  const lastConsumedLinkRef = useRef<string | null>(null);

  useEffect(() => {
    const linkKey = `${location.search}${location.hash}`;
    if (lastConsumedLinkRef.current === linkKey) {
      return;
    }
    lastConsumedLinkRef.current = linkKey;
    setMagicLinkFailed(false);

    const mountedAt = performance.now();
    const params = new URLSearchParams(location.search);
    const hasPreAuthSessionId = params.has('preAuthSessionId');
    const tenantIdParam = params.get('tenantId');

    const verifyMagicLink = async () => {
      logger.info('MagicLinkVerifyPage loaded, starting verification flow');

      try {
        logger.info('Signing out of current session');
        await Session.signOut();
        logger.info('Successfully signed out of current session');
      } catch (error) {
        logger.error('Error signing out of the current session', {
          error: formatErrorForLogging(error),
        });
        setMagicLinkFailed(true);
        return;
      }

      try {
        logger.info('Consuming magic link code');

        const response = await consumeCodeRetry.execute(() =>
          Passwordless.consumeCode(),
        );

        logger.info('Magic link code consumption response received', {
          status: response.status,
        });

        if (response.status === 'OK') {
          logger.info('Magic link code consumed successfully');

          let redirectTo = params.get('redirectTo') ?? '/';

          if (!redirectTo.startsWith('/')) {
            redirectTo = `/${redirectTo}`;
          }

          logger.info('Extracted redirect target', { redirectTo });

          // Refresh claims to capture tenant auto-approval
          try {
            logger.info('Refreshing allowed tenants');
            await apiClient.auth.refreshAllowedTenants({ body: {} });
            logger.info('Successfully refreshed allowed tenants');
          } catch (error) {
            logger.warn(
              'Failed to refresh allowed tenants after magic link verification',
              { error: formatErrorForLogging(error) },
            );
          }

          logger.info('Login successful, redirecting to:', { redirectTo });
          navigate(redirectTo);
        } else {
          logger.warn('Magic link verification was not successful', {
            status: response.status,
            response,
          });
          setMagicLinkFailed(true);
        }
      } catch (error) {
        // Show the failure UI before the (bounded) body read — a slow error
        // response must not leave the page stuck on the loading state
        setMagicLinkFailed(true);
        const responseDetails = await extractResponseErrorDetails(error);
        logger.error('Error consuming magic link', {
          error: formatErrorForLogging(error),
          message:
            error instanceof Error
              ? error.message
              : formatErrorForLogging(error),
          errorName: getErrorName(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          responseStatus: responseDetails?.status,
          responseStatusText: responseDetails?.statusText,
          responseUrl: responseDetails?.url,
          responseBody: responseDetails?.body,
          errorCause:
            error instanceof Error && error.cause !== undefined
              ? formatErrorForLogging(error.cause)
              : undefined,
          online: navigator.onLine,
          connectionType: getConnectionEffectiveType(),
          hasPreAuthSessionId,
          tenantIdParam,
          elapsedMs: Math.round(performance.now() - mountedAt),
        });
      }
    };

    verifyMagicLink();
  }, [navigate, logger, location, apiClient]);

  // Auto-redirect to login page after 5 seconds when there's an error
  useEffect(() => {
    if (magicLinkFailed) {
      logger.info('Magic link failed, starting auto-redirect countdown (5s)');
      const timer = setTimeout(() => {
        logger.info('Auto-redirecting to login page');
        navigate('/login');
      }, 5000);

      // Countdown timer
      const countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownTimer);
      };
    }
  }, [magicLinkFailed, navigate]);

  const handleGoToLogin = () => {
    logger.info('User manually clicked "Go to Login" button');
    navigate('/login');
  };

  if (magicLinkFailed) {
    logger.info('Rendering error UI');
    return (
      <div className="grid h-full place-items-center p-4">
        <div className="max-w-md space-y-4">
          <Alert variant="destructive">
            <Icon name="AlertCircle" size="xl" className="size-4" />
            <AlertTitle>Login Failed</AlertTitle>
            <AlertDescription>
              Unable to verify your login link. Please try again.
            </AlertDescription>
          </Alert>
          <div className="space-y-2 text-center">
            <Button onClick={handleGoToLogin}>Go to Login</Button>
            <Text color="tertiary" weight="regular">
              You will be automatically redirected to the login page in{' '}
              {countdown}
              {countdown === 1 ? ' second' : ' seconds'}.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  logger.info('Rendering loading UI');
  return (
    <div className="grid h-full place-items-center">
      <LoadingIcon className="text-border-accent-quaternary" size="2xl" />
    </div>
  );
};
